export function salePriceState({ price, compareAtPrice = 0, fixedPrice, startsAt, endsAt, now, managedPrice = false, catalogPrice = price, catalogMode = false }) {
  const inputs = [price, fixedPrice, startsAt, endsAt, now];
  if (!inputs.every(Number.isFinite) || !Number.isInteger(price) || !Number.isInteger(fixedPrice) || price <= 0 || fixedPrice <= 0 || fixedPrice >= price || endsAt <= startsAt) return { status: 'invalid' };
  const regularPrice = Math.max(price, compareAtPrice || 0);
  const status = now < startsAt ? 'scheduled' : now >= endsAt ? managedPrice && catalogPrice !== price ? 'restoring' : 'expired' : catalogMode && catalogPrice !== fixedPrice ? 'preparing' : 'active';
  return { status, price: fixedPrice, regularPrice, saving: regularPrice - fixedPrice, percentage: Math.round((regularPrice - fixedPrice) * 1000 / regularPrice) / 10 };
}

export function countdownParts(endsAt, now) {
  let seconds = Math.max(0, Math.ceil((endsAt - now) / 1000));
  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  return { days, hours, minutes: Math.floor(seconds / 60), seconds: seconds % 60 };
}

if (typeof customElements !== 'undefined' && !customElements.get('lumino-sale-countdown')) {
  class LuminoSaleCountdown extends HTMLElement {
    connectedCallback() {
      this.configuration = JSON.parse(this.querySelector('[data-sale-configuration]').textContent);
      this.timer = this.querySelector('[data-sale-timer]');
      this.offer = this.querySelector('[data-sale-offer]');
      this.fallback = this.querySelector('[data-sale-fallback]');
      this.statusText = this.querySelector('[data-sale-status]');
      this.endDate = this.querySelector('[data-sale-end-date]');
      const c = this.configuration;
      if (Number.isFinite(c.endsAt) && c.endsAt > 0) this.endDate.textContent = new Intl.DateTimeFormat(c.locale, { timeZone: 'Asia/Tbilisi', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(c.endsAt));
      this.tick = this.tick.bind(this);
      this.onVisibility = () => {
        clearInterval(this.interval);
        this.tick();
        if (!document.hidden && this.state.status !== 'expired' && this.state.status !== 'invalid') this.interval = setInterval(this.tick, 1000);
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onVisibility();
    }

    tick() {
      this.state = salePriceState({ ...this.configuration, now: Date.now() });
      const active = this.state.status === 'active';
      this.offer.hidden = !active;
      this.timer.hidden = !active;
      this.fallback.hidden = active || (!this.configuration.catalogMode && this.state.status === 'restoring');
      this.statusText.textContent = active ? '' : this.configuration.messages[this.state.status];
      this.statusText.hidden = active;
      if (active) {
        const parts = countdownParts(this.configuration.endsAt, Date.now());
        for (const [name, value] of Object.entries(parts)) this.querySelector(`[data-sale-${name}]`).textContent = String(value).padStart(2, '0');
      } else if (this.state.status === 'restoring' || this.state.status === 'preparing') {
        this.refreshCatalog();
      } else if (this.state.status === 'expired' || this.state.status === 'invalid') {
        clearInterval(this.interval);
      }
    }

    async refreshCatalog() {
      if (this.refreshing || Date.now() - (this.lastRefresh || 0) < 5000) return;
      this.refreshing = true;
      this.lastRefresh = Date.now();
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('variant', this.dataset.variantId);
        url.searchParams.set('_sale_check', String(this.lastRefresh));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok || !this.isConnected) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = html.querySelector('lumino-sale-countdown');
        if (!replacement) return;
        const config = JSON.parse(replacement.querySelector('[data-sale-configuration]').textContent);
        const state = salePriceState({ ...config, now: Date.now() });
        if (state.status !== 'invalid' && (state.status !== this.state.status || config.catalogPrice !== this.configuration.catalogPrice) && this.isConnected) this.replaceWith(document.importNode(replacement, true));
      } catch {
        // Keep the pending state and retry until Shopify confirms its catalog price.
      } finally {
        this.refreshing = false;
      }
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }
  customElements.define('lumino-sale-countdown', LuminoSaleCountdown);
}
